import _ from 'underscore';
import Backbone from 'backbone';
import Promise from 'bluebird';
import { FilterI18n } from '@bingads-webui/filter-i18n';
import * as putil from '@bingads-webui-universal/primitive-utilities';
import filterBarTemplate from './filter-bar-template.pug';
import singleTemplate from './single.pug';
import listTemplate from './list.pug';

/**
 * FilterBar Class
 * It displays the query details of a filter
 */
class FilterBar extends Backbone.View {
  /**
   * Get FilterBar's CSS class name
   */
  get className() {
    return 'filter-bar';
  }

  /**
   * Get all events
   */
  get events() {
    return {
      'click .filter-bar__edit-filter-link': this.onEdit,
      'click .filter-bar__remove-filter-link': this.onRemove,
    };
  }

  initialize({
    options = {},
    i18n,
  }) {
    this.i18n = i18n;
    this.options = _.defaults(options || {}, {
      title: i18n.getString('GridToolbar_FilterBar_Title'),
      editText: i18n.getString('GridToolbar_FilterBar_Edit'),
      removeText: i18n.getString('GridToolbar_FilterBar_Remove'),
      loadingText: i18n.getString('GridToolbar_FilterBar_Loading'),
      loadingErrorText: i18n.getString('GridToolbar_FilterBar_LoadingError'),
      loading: false,
      error: undefined,
      humanizedFilterHtml: '',
      hideEdit: false,
      hideRemove: false,
    });
    this.setFilter = putil.withLastPromise({
      promiseFactory: _.bind((filter) => {
        this.options.error = undefined;
        this.options.loading = true;
        this.render();
        if (this.options.customHumanizedHtml) {
          return this.options.customHumanizedHtml(this, filter);
        }
        return this.humanizedHtml(filter);
      }, this),
      finish: _.bind((humanizedFilterHtml) => {
        this.options.loading = false;
        this.options.humanizedFilterHtml = humanizedFilterHtml;
        this.render();
      }, this),
      fail: _.bind((error) => {
        this.options.loading = false;
        this.options.error = error;
        this.options.humanizedFilterHtml = '';
        this.render();
      }, this),
    });
    this.changeFilter(this.options.filter);
  }

  changeFilter(filter) {
    if (!filter) {
      // trying to apply empty filter
      // this can happen if saved filter does not have valid values
      // for example, custom column filter in legacy UI.
      _.defer(() => {
        this.onRemove();
      });
      return;
    }

    this.options.filter = filter;
    this.setFilterProcess = this.setFilter(filter);
  }

  onEdit() {
    this.trigger('edit', this);
  }

  onRemove() {
    this.options.filter = null;
    this.trigger('remove', this);
  }

  render() {
    this.$el.html(filterBarTemplate(this.options));

    return this;
  }

  remove() {
    if (this.setFilterProcess) {
      this.setFilterProcess.cancel();
      this.setFilterProcess = null;
    }

    Backbone.View.prototype.remove.call(this);
  }

  getValuesAndMoreTextForSelector(valuesInQuery, property) {
    const MAX_ITEMS_TO_SHOW = 5;
    const ITEMS_MAX_LENGTH = 50;
    let itemsPromise;
    if (property.dataSource && !_.isEmpty(valuesInQuery)) {
      // Asking for one over the limit so that "and more" logic can be triggered
      itemsPromise = property.dataSource.getByIds(valuesInQuery.slice(0, MAX_ITEMS_TO_SHOW + 1));
    } else if (property.items) {
      itemsPromise = Promise.resolve(property.items);
    } else {
      itemsPromise = Promise.resolve([]);
    }

    return itemsPromise
      .then((items) => {
        const idInfoMap = _.indexBy(items, 'Id');
        const { names } = _.reduce(valuesInQuery, (memo, id) => {
          const memoCopy = memo;
          if (_.size(memoCopy.names) < MAX_ITEMS_TO_SHOW
          && memoCopy.totalLength < ITEMS_MAX_LENGTH) {
            memoCopy.names.push(_.has(idInfoMap, id) ? idInfoMap[id].Name : this.i18n.getString('Filter_Item_Deleted'));
            memoCopy.totalLength += _.size(_.last(memoCopy.names));
          }

          return memoCopy;
        }, { totalLength: 0, names: [] });

        const notShowingCount = _.size(valuesInQuery) - _.size(names);

        return {
          values: names,
          moreText: notShowingCount ? this.i18n.getString('And_More', {
            remain: notShowingCount,
          }) : null,
        };
      });
  }

  extendPredicate(predicate) {
    const property = _.keys(predicate)[0];
    const metadata = this.filterI18n.schema.getFilterProperty(property);
    if (!metadata) {
      throw new Error(`Property ${property} is not filterable`);
    }
    const opValue = _.values(predicate)[0];
    const operator = _.keys(opValue)[0].replace(/^\$/, '');
    const value = _.values(opValue)[0];
    const schemaI18n = {
      property: metadata.title ? metadata.title : this.filterI18n.convertName(property),
      operator: this.filterI18n.convertOperator(operator, metadata.operatorDisplayNameMap),
      values: _.map(
        _.isArray(value) ? value : [value],
        val => this.filterI18n.convertValue(val, property)
      ),
    };

    let i18nPromise;
    if (metadata.type === 'id') { // for selector type, override localization (values are ids here, names are dynamic values for each selector)
      i18nPromise = this.getValuesAndMoreTextForSelector(value, metadata)
        .then(foo => _.extend(schemaI18n, foo));
    } else {
      i18nPromise = Promise.resolve(schemaI18n);
    }

    return i18nPromise.then(i18n => _.extend({}, predicate, {
      i18n,
    }));
  }

  extendFilter(filter) {
    if (filter.$and) {
      return Promise.map(filter.$and, _.bind(this.extendFilter, this))
        .then($and => ({ $and }));
    } else if (filter.$or) {
      return Promise.map(filter.$or, _.bind(this.extendFilter, this))
        .then($or => ({ $or }));
    }
    return this.extendPredicate(filter);
  }

  humanizedHtml(filter) {
    if (!filter.query || !filter.depth) {
      throw new Error('Incorrect filter object');
    }

    if (filter.depth < 1 || filter.depth > 2) {
      throw new Error('Filter only support 2 levels');
    }

    this.filterI18n = new FilterI18n(
      filter.schema,
      this.i18n,
      this.options.schemaI18n,
      filter.options.shortDateFormat
    );

    const depthTemplateMap = {
      1: singleTemplate,
      2: listTemplate,
    };

    return this.extendFilter(filter.query)
      .then((extended) => {
        const template = depthTemplateMap[filter.depth];

        return template({
          filter: extended,
        });
      });
  }
}

export default FilterBar;
